import { useState } from "react";
import { ShieldCheck } from "lucide-react";

import { APIError, verifyTotp } from "@/lib/api";
import { humanizeError } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/ui/field";
import { Spinner } from "@/components/Page";

// TwoFactor is the second-factor login step. It renders when the user has
// passed the first factor (a cb_2fa challenge cookie is set) but isn't yet
// signed in. On success it calls onVerified, which refetches /v1/me and
// drops the user into the app.
export function TwoFactor({ onVerified }: { onVerified: () => void }) {
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState(false);
  const [err, setErr] = useState("");
  const [expired, setExpired] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      await verifyTotp(code.trim());
      onVerified();
      return; // App re-renders into the signed-in shell.
    } catch (e2) {
      if (e2 instanceof APIError && e2.code === "challenge_expired") {
        setExpired(true);
      } else {
        setErr(humanizeError(e2, "That code didn't match. Try again."));
      }
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <div className="mx-auto mb-1 flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <CardTitle className="text-xl">Two-step verification</CardTitle>
          <CardDescription>
            {expired
              ? "This verification expired."
              : recovery
                ? "Enter one of your recovery codes."
                : "Enter the 6-digit code from your authenticator app."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {expired ? (
            <Button asChild className="w-full">
              <a href="/">Back to sign in</a>
            </Button>
          ) : (
            <>
              <form onSubmit={submit} className="space-y-3" noValidate>
                <Field label={recovery ? "Recovery code" : "Authenticator code"} htmlFor="code" error={err}>
                  <Input
                    id="code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    inputMode={recovery ? "text" : "numeric"}
                    autoComplete="one-time-code"
                    placeholder={recovery ? "XXXXX-XXXXX" : "123456"}
                    maxLength={recovery ? 11 : 6}
                    autoFocus
                    aria-invalid={!!err}
                    className={recovery ? "" : "text-center tracking-[0.4em]"}
                  />
                </Field>
                <Button type="submit" className="w-full" disabled={submitting || !code.trim()}>
                  {submitting ? <Spinner /> : null} {submitting ? "Verifying…" : "Verify"}
                </Button>
              </form>
              <button
                type="button"
                onClick={() => {
                  setRecovery((v) => !v);
                  setErr("");
                  setCode("");
                }}
                className="w-full text-center text-xs text-muted-foreground hover:text-foreground"
              >
                {recovery ? "Use your authenticator app instead" : "Use a recovery code instead"}
              </button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
